import * as Sentry from '@sentry/react';
import { emitFrontendTelemetry } from './frontendTelemetry';

const testLogLevel = process.env.HRP_TEST_LOG_LEVEL || 'warn';
const isTest = process.env.NODE_ENV === 'test';
const shouldEmitTestLogs = process.env.HRP_TEST_EMIT_LOGS === '1' || testLogLevel === 'debug';

/**
 * Returns true when the argument is an EventSource / EventSourcePolyfill
 * error event object.  These carry the full request headers (including
 * Authorization JWT) inside `target.headers` — stringifying them leaks
 * credentials into Sentry.  Detection: `type === 'error'` with a `target`
 * that has a numeric `readyState` and a string `url` (the polyfill shape).
 */
function isEventSourceErrorEvent(arg) {
    return (
        arg != null &&
        typeof arg === 'object' &&
        arg.type === 'error' &&
        arg.target != null &&
        typeof arg.target === 'object' &&
        typeof arg.target.url === 'string' &&
        typeof arg.target.readyState === 'number'
    );
}

/**
 * Produce a safe summary string from an EventSource error event,
 * stripping credentials and keeping only the URL pathname + readyState.
 */
function sanitizeEventSourceError(arg) {
    let pathname = arg.target.url;
    try {
        pathname = new URL(arg.target.url).pathname;
    } catch (_e) {
        // keep raw url if unparseable (shouldn't happen)
    }
    return `SSE connection error (readyState: ${arg.target.readyState}, url: ${pathname})`;
}

function toError(args) {
    const existingError = args.find((arg) => arg instanceof Error);

    if (existingError) {
        return existingError;
    }

    const message = args
        .map((arg) => {
            if (typeof arg === 'string') return arg;
            if (arg === null || arg === undefined) return String(arg);

            // Defence-in-depth: never JSON.stringify EventSource error events —
            // they embed Authorization headers that contain JWTs.
            if (isEventSourceErrorEvent(arg)) {
                return sanitizeEventSourceError(arg);
            }

            try {
                return JSON.stringify(arg);
            } catch (_error) {
                return String(arg);
            }
        })
        .join(' ');

    return new Error(message || 'Frontend logger error');
}

const isProduction = process.env.NODE_ENV === 'production';
const shouldEmitInfoLogs = !isProduction && (!isTest || shouldEmitTestLogs);
// Test bootstrap mutes console output directly, so warn/error should still call
// console methods in tests to keep existing spies and assertions working.
const shouldEmitWarnErrorLogs = true;

const ARCHIVE_MANAGEMENT_PATH = '/report/project-archive-management/';

/**
 * Archive, unarchive and archive-date changes answer controlled business-rule
 * refusals (already archived, active recognition lock, Mastercard readiness, an
 * existing reclass journal, ...) as 409 with a stable `code`. The backend logs
 * those at warn and every caller shows the server message to the user, so the
 * resulting AxiosError is expected feedback, not an unexpected exception.
 * A 409 without a code, or from any other endpoint, is still reported.
 */
function isControlledArchiveRejection(error) {
    return error?.response?.status === 409 &&
        typeof error.response.data?.code === 'string' &&
        error.response.data.code !== '' &&
        String(error.config?.url || '').includes(ARCHIVE_MANAGEMENT_PATH);
}

function shouldSkipCapture(error) {
    const status = error?.response?.status;
    const message = error?.message || '';

    // 400: validation errors (expected, handled by components)
    // 401: session expiry / missing token (expected, interceptor redirects to login)
    // 403: permission denied (expected for role-restricted endpoints)
    // 422: semantic validation errors (expected, handled by components)
    // 429: rate limited (transient, expected under load — Axios retries automatically;
    //      native fetch SSE callers surface a user-facing toast)
    // 5xx: backend errors — already captured by backend Sentry with full server context;
    //      duplicate frontend events with minified stack traces add no debugging value
    if (status === 400 || status === 401 || status === 403 || status === 422 || status === 429 || (status >= 500 && status <= 599)) return true;
    // Message-based fallback for native fetch() errors which lack error.response
    // (e.g. SSE stream callers throw `new Error(\`HTTP ${status}: ${statusText}\`)`)
    if (/status code (400|401|403|422|429|5\d\d)/i.test(message)) return true;
    if (/\bHTTP 429\b/i.test(message)) return true;
    if (/\bHTTP 5\d\d\b/i.test(message)) return true;

    // XERO_WORK_LOCK_BUSY: expected 409 lock contention (another Xero sync is in progress)
    // This is an intentional operational outcome, not an application bug.
    if (error?.response?.data?.error === 'XERO_WORK_LOCK_BUSY') return true;

    // Controlled project archive/unarchive refusals (409 + code) are user-facing feedback.
    if (isControlledArchiveRejection(error)) return true;

    // Voice realtime quota/rate-limit errors are external provider billing
    // issues, not application bugs. Skip Sentry as defense-in-depth so that
    // even if future voice code paths forget to classify, these errors are
    // still filtered out.
    if (/\binsufficient_quota\b|\brate_limit_exceeded\b/i.test(message)) return true;

    return false;
}

/**
 * Returns true when any argument in the logger.error call is an Ant Design
 * form validation rejection object (has an `errorFields` array). These are
 * expected user-facing validation events — never forward them to Sentry.
 */
function hasFormValidationError(args) {
    return args.some((arg) => arg != null && typeof arg === 'object' && Array.isArray(arg.errorFields));
}

const logger = {
    error: (...args) => {
        if (shouldEmitWarnErrorLogs) {
            console.error(...args);
        }
        // Ant Design form validation rejections are user-facing feedback, not
        // unexpected exceptions — skip Sentry before constructing an Error.
        if (hasFormValidationError(args)) {
            return;
        }
        // EventSource / SSE connection failures are transient and user-
        // recoverable (the component already shows a toast).  Forwarding them
        // to Sentry leaks the JWT from the polyfill's Authorization header
        // and creates false-positive noise.
        if (args.some(isEventSourceErrorEvent)) {
            return;
        }
        const error = toError(args);
        if (!shouldSkipCapture(error)) {
            Sentry.captureException(error);
        }
    },
    warn: (...args) => {
        if (shouldEmitWarnErrorLogs) {
            console.warn(...args);
        }
    },
    info: (...args) => {
        if (shouldEmitInfoLogs) {
            console.log(...args);
        }
    },
    debug: (...args) => {
        if (shouldEmitInfoLogs) {
            console.log(...args);
        }
    },
    workflowFailure: (context = {}, error) => {
        const telemetryError = error instanceof Error ? error : toError([error]);

        logger.error(context.message || 'Workflow failure', telemetryError);
        void emitFrontendTelemetry('frontend.workflow.failure', {
            area: context.area || 'workflow',
            action: context.action || 'failed',
            statusCode: telemetryError?.response?.status,
            requestId: telemetryError?.response?.headers?.['x-request-id'],
            errorCode: telemetryError?.code || telemetryError?.name,
            message: telemetryError?.message || context.message,
            url: context.url,
            method: context.method,
        });
    },
    reportFailure: (context = {}, error) => {
        const telemetryError = error instanceof Error ? error : toError([error]);
        void emitFrontendTelemetry('frontend.report.failure', {
            area: context.area || 'report',
            action: context.action || 'generation_failed',
            statusCode: telemetryError?.response?.status,
            requestId: telemetryError?.response?.headers?.['x-request-id'],
            errorCode: telemetryError?.code || telemetryError?.name,
            message: telemetryError?.message || context.message,
            url: context.url,
            method: context.method,
            durationMs: context.durationMs,
        });
    },
    exportFailure: (context = {}, error) => {
        const telemetryError = error instanceof Error ? error : toError([error]);
        void emitFrontendTelemetry('frontend.export.failure', {
            area: context.area || 'export',
            action: context.action || 'download_failed',
            statusCode: telemetryError?.response?.status,
            requestId: telemetryError?.response?.headers?.['x-request-id'],
            errorCode: telemetryError?.code || telemetryError?.name,
            message: telemetryError?.message || context.message,
            url: context.url,
            method: context.method,
            durationMs: context.durationMs,
        });
    },
    slowView: (context = {}) => {
        void emitFrontendTelemetry('frontend.performance.slow_view', {
            area: context.area || 'performance',
            action: context.action || 'slow_view',
            message: context.message || context.metricName || 'Slow frontend view detected',
            durationMs: context.durationMs,
            url: context.url,
            statusCode: context.statusCode,
        });
    },
};

export default logger;
